// defineVariablesBlocks.ts
import * as Blockly from 'blockly/core';
import { javascriptGenerator } from 'blockly/javascript';

// On sauvegarde l'ancienne méthode .finish
const oldFinish = javascriptGenerator.finish;
javascriptGenerator.finish = function (code) {
    // Appel de l’original
    code = oldFinish.call(this, code);
    // On retire toute déclaration "var x, y;"
    code = code.replace(/^var\s+[^\n]*\n/, '');
    return code;
};

/**
 * Déclare des blocs pour manipuler les variables :
 *   - variables_declare
 *   - variables_set
 *   - variables_increment
 *   - variables_get
 */
export function defineVariablesBlocks() {
    // Liste dynamique des variables déclarées via les blocs 'variables_declare'
    const getDeclareBlocks = (workspace: Blockly.Workspace | null | undefined) => {
        if (!workspace) return [] as Blockly.Block[];
        return workspace.getAllBlocks(false).filter((b) => b.type === 'variables_declare');
    };

    // Options [label, value] où value = id du bloc de déclaration
    const getDeclaredOptions = (
        workspace: Blockly.Workspace | null | undefined
    ): [string, string][] => {
        const declares = getDeclareBlocks(workspace);
        if (!declares.length) return [['<aucune variable>', '']];
        return declares.map((b) => {
            const name = (b.getField('VAR_NAME')?.getText?.() as string) || 'myVar';
            return [name, b.id];
        });
    };

    /***********************************************
     * 1) variables_declare (déclarer var = value)
     ***********************************************/
    Blockly.Blocks['variables_declare'] = {
        init: function () {
            this.appendDummyInput().appendField('déclarer');
            // Champ variable (texte libre pour éviter le renommage global de Blockly)
            this.appendDummyInput('VAR').appendField(
                new Blockly.FieldTextInput(`myVar_${this.id.substr(-3)}`),
                'VAR_NAME'
            );

            // Valeur
            this.appendValueInput('VALUE').setCheck(null).appendField('=');

            this.setPreviousStatement(true, null);
            this.setNextStatement(true, null);
            this.setColour(330);
            this.setTooltip("Déclare une nouvelle variable et lui assigne une valeur.");
            this.setHelpUrl('');
        },
    } as any;

    javascriptGenerator.forBlock['variables_declare'] = function (block, generator) {
        const varName = block.getField('VAR_NAME')?.getText() || 'myVar';
        const valueCode = generator.valueToCode(block, 'VALUE', 0) || 'undefined';
        const code = `let ${varName} = ${valueCode};\n`;
        return code;
    };

    /***********************************************
     * 2) variables_set (mettre var = value)
     ***********************************************/
    Blockly.Blocks['variables_set'] = {
        init: function () {
            this.appendDummyInput().appendField('mettre');
            this.appendDummyInput().appendField(
                new Blockly.FieldDropdown(() => getDeclaredOptions((this as any).workspace)),
                'VAR_REF'
            );
            this.appendValueInput('VALUE').setCheck(null).appendField('=');

            this.setPreviousStatement(true, null);
            this.setNextStatement(true, null);
            this.setColour(330);
            this.setTooltip('Assigne une nouvelle valeur à une variable existante.');
            this.setHelpUrl('');
        },
    } as any;

    javascriptGenerator.forBlock['variables_set'] = function (block, generator) {
        const declId = block.getFieldValue('VAR_REF');
        const decl = declId ? block.workspace.getBlockById(declId) : null;
        const varName = (decl && decl.getField('VAR_NAME')?.getText()) || 'myVar';
        const valueCode = generator.valueToCode(block, 'VALUE', 0) || 'undefined';
        return `${varName} = ${valueCode};\n`;
    };

    /***********************************************
     * 3) variables_increment (changer var de value)
     * => var = var + value
     ***********************************************/
    Blockly.Blocks['variables_increment'] = {
        init: function () {
            this.appendDummyInput().appendField('Incrémente');
            this.appendDummyInput().appendField(
                new Blockly.FieldDropdown(() => getDeclaredOptions((this as any).workspace)),
                'VAR_REF'
            );
            this.appendValueInput('DELTA').setCheck('Number').appendField('de');

            this.setPreviousStatement(true, null);
            this.setNextStatement(true, null);
            this.setColour(330);
            this.setTooltip("Ajoute la valeur spécifiée à la variable (ex: var += delta).");
            this.setHelpUrl('');
        },
    } as any;

    javascriptGenerator.forBlock['variables_increment'] = function (block, generator) {
        const declId = block.getFieldValue('VAR_REF');
        const decl = declId ? block.workspace.getBlockById(declId) : null;
        const varName = (decl && decl.getField('VAR_NAME')?.getText()) || 'myVar';
        const deltaCode = generator.valueToCode(block, 'DELTA', 0) || '0';
        return `${varName} += ${deltaCode};\n`;
    };

    /***********************************************
     * 4) variables_decrement (changer var de value)
     ***********************************************/
    Blockly.Blocks['variables_decrement'] = {
        init: function () {
            this.appendDummyInput().appendField('Décrémente');
            this.appendDummyInput().appendField(
                new Blockly.FieldDropdown(() => getDeclaredOptions((this as any).workspace)),
                'VAR_REF'
            );
            this.appendValueInput('DELTA').setCheck('Number').appendField('de');

            this.setPreviousStatement(true, null);
            this.setNextStatement(true, null);
            this.setColour(330);
            this.setTooltip("Soustrait la valeur spécifiée à la variable (ex: var -= delta).");
            this.setHelpUrl('');
        },
    } as any;

    javascriptGenerator.forBlock['variables_decrement'] = function (block, generator) {
        const declId = block.getFieldValue('VAR_REF');
        const decl = declId ? block.workspace.getBlockById(declId) : null;
        const varName = (decl && decl.getField('VAR_NAME')?.getText()) || 'myVar';
        const deltaCode = generator.valueToCode(block, 'DELTA', 0) || '0';
        return `${varName} -= ${deltaCode};\n`;
    };

    /***********************************************
     * 5) variables_get (obtenir var)
     * => un bloc output
     ***********************************************/
    Blockly.Blocks['variables_get'] = {
        init: function () {
            this.appendDummyInput().appendField(
                new Blockly.FieldDropdown(() => getDeclaredOptions((this as any).workspace)),
                'VAR_REF'
            );
            this.setOutput(true, null);
            this.setColour(330);
            this.setTooltip('Renvoie la valeur de la variable.');
            this.setHelpUrl('');
        },
    } as any;

    javascriptGenerator.forBlock['variables_get'] = function (block) {
        const declId = block.getFieldValue('VAR_REF');
        const decl = declId ? block.workspace.getBlockById(declId) : null;
        const varName = (decl && decl.getField('VAR_NAME')?.getText()) || 'myVar';
        return [varName, 0];
    };
}

